import { Combobox, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import { FC, useState, Fragment } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Patient,
  usePatientsV2NamesQuery,
  PatientLifecycleStatusV2,
} from '../../generated/graphql';
import Avatar from './Avatar';
import { sortPatientsByAlphabeticalAscending } from './PatientItem';

interface PatientSelectorProps {
  onChange: (patient: Patient | null) => void;
  value: Patient | null;
  placeholder?: string;
  errorMessage?: string;
  required?: boolean;
}

const PatientSelector: FC<PatientSelectorProps> = ({
  onChange,
  value,
  placeholder = 'Search clients',
  errorMessage,
  required = false,
}) => {
  const { authedProviderUser } = useAuth();
  const { data: patientsData } = usePatientsV2NamesQuery({
    variables: {
      patientLifecycleStatuses: [
        PatientLifecycleStatusV2.Invited,
        PatientLifecycleStatusV2.Active,
      ],
      assignedPatients: true,
    },
  });

  const patientsList = sortPatientsByAlphabeticalAscending(
    patientsData?.patientNames?.filter((patient) =>
      authedProviderUser?.hasPremiumAccess ? true : patient.isTestProgram,
    ),
  );

  const [query, setQuery] = useState('');

  const filteredPatients = patientsList.filter((patient) => {
    if (query) {
      return (
        patient?.firstName?.toLowerCase().includes(query.toLowerCase()) ||
        patient?.lastName?.toLowerCase().includes(query.toLowerCase())
      );
    }
    return true;
  });

  return (
    <div className="w-full">
      <Combobox
        as="div"
        className="relative w-full text-body"
        onChange={onChange}
        value={value}
      >
        <Combobox.Button className="relative flex w-full items-center">
          <Combobox.Input
            className={classNames(
              'relative flex w-full cursor-default flex-row items-center justify-start text-body placeholder:text-body',
              'max-h-14 rounded-md bg-neutral-50 p-4 text-left text-green-150 shadow-sm sm:text-caption',
              'border-0 placeholder:text-neutral-125/75 focus:border-green-50 focus:outline-none focus:ring-2 focus:ring-secondary-50',
              errorMessage &&
                'border-red-100 focus:border-red-100 focus:ring-red-50',
            )}
            placeholder={placeholder}
            displayValue={(patient: Patient) =>
              patient ? `${patient?.firstName} ${patient?.lastName}` : undefined
            }
            onChange={(event) => setQuery(event.target.value)}
          />
          <ChevronDownIcon
            className="absolute right-3 h-4 w-4 text-neutral-125"
            aria-hidden="true"
          />
        </Combobox.Button>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full divide-y divide-neutral-75 overflow-auto rounded-lg border border-neutral-75 bg-white py-1 text-caption shadow-lg focus:outline-none focus:ring-0">
            {filteredPatients.map((patient) => {
              return (
                <Combobox.Option
                  key={patient.programInstanceId}
                  value={patient}
                >
                  {({ active, selected }) => {
                    const patientName = `${patient?.firstName} ${patient?.lastName}`;
                    return (
                      <div
                        className={classNames(
                          'relative flex cursor-pointer select-none flex-row truncate py-3 px-5 text-green-150 hover:bg-neutral-25',
                          {
                            'bg-neutral-25': selected || active,
                          },
                        )}
                      >
                        <Avatar
                          size="small"
                          name={patientName}
                          imageUrl={patient?.profileImageMedia?.url}
                          className="mr-2"
                        />
                        {patientName}
                      </div>
                    );
                  }}
                </Combobox.Option>
              );
            })}
          </Combobox.Options>
        </Transition>
      </Combobox>
      {errorMessage && (
        <p className="mt-1 text-small-caption text-red-100">{errorMessage}</p>
      )}
    </div>
  );
};

export default PatientSelector;
